/**
 * Create or edit a department (name, description, active flag). Failures show inline and as a
 * toast; success toasts and refreshes every department query.
 */

import { useEffect } from 'react';
import { useForm } from '@mantine/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Stack, Group } from '@/components/ui/layout';
import { Button, Text } from '@/components/ui/core';
import { TextInput, Textarea, Switch } from '@/components/ui/inputs';
import { Drawer } from '@/components/ui/overlays';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useDepartmentService, type Department, type DepartmentModelView } from '@actschurch/ccms-sdk';
import { invalidateDepartmentQueries } from './departmentsKeys';
import { errorMessage } from './departmentsUtils';
import { ErrorNotice } from './DepartmentsUi';

interface DepartmentFormDrawerProps {
  opened: boolean;
  onClose: () => void;
  /** Editing when set; creating otherwise. */
  department?: Pick<DepartmentModelView, 'departmentID' | 'name' | 'description' | 'isActive'> | null;
  onSaved?: (saved: Department) => void;
}

export default function DepartmentFormDrawer({ opened, onClose, department, onSaved }: DepartmentFormDrawerProps) {
  const isEditMode = !!department;
  const departmentService = useDepartmentService();
  const queryClient = useQueryClient();

  const form = useForm({
    initialValues: { name: '', description: '', isActive: true },
    validate: {
      name: (value) => (!value?.trim() ? 'Name is required' : value.trim().length > 100 ? 'Keep the name under 100 characters' : null),
    },
  });

  useEffect(() => {
    if (!opened) return;
    if (department) {
      form.setValues({ name: department.name || '', description: department.description || '', isActive: department.isActive });
    } else {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, department]);

  const save = useMutation({
    mutationFn: (values: typeof form.values) =>
      isEditMode
        ? departmentService.update(department!.departmentID, { name: values.name.trim(), description: values.description.trim() || null, isActive: values.isActive })
        : departmentService.create({ name: values.name.trim(), description: values.description.trim() || null }),
    onSuccess: (saved) => {
      invalidateDepartmentQueries(queryClient);
      toast.success(isEditMode ? 'Department updated' : 'Department created', saved.name);
      onSaved?.(saved);
      onClose();
    },
    onError: (err) => toast.error(isEditMode ? 'Could not update the department' : 'Could not create the department', errorMessage(err)),
  });

  const handleClose = () => {
    if (save.isPending) return;
    save.reset();
    onClose();
  };

  return (
    <Drawer opened={opened} onClose={handleClose} title={isEditMode ? 'Edit department' : 'New department'} position="right" size="md">
      <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
        <Stack gap="md">
          <TextInput label="Name" placeholder="For example Finance" required data-autofocus {...form.getInputProps('name')} />
          <Textarea label="Description" placeholder="What this department looks after" rows={4} {...form.getInputProps('description')} />
          {isEditMode && (
            <Switch
              label="Active"
              description="Inactive departments are hidden from pickers in Tasks, Budgets and Forms. Members stay assigned."
              checked={form.values.isActive}
              onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
            />
          )}
          <ErrorNotice error={save.error} title={isEditMode ? 'Could not update the department' : 'Could not create the department'} />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={save.isPending}>
              {isEditMode ? 'Save changes' : 'Create department'}
            </Button>
          </Group>
          {!isEditMode && (
            <Text size="xs" c="dimmed">
              You can add members and designations once the department exists.
            </Text>
          )}
        </Stack>
      </form>
    </Drawer>
  );
}
